import { useState } from "react"
import { createPortal } from "react-dom"
import classNames from "classnames"

import { FeatureFlag, isFeatureEnabled } from "store/featureFlagsSlice"
import { useAppSelector } from "store/hooks"
import { ApiTypes } from "types"
import TemperatureScale from "types/enums/TemperatureScale"
import { MonitoringReportAnnotations } from "types/MonitoringReportAnnotations"

import { useUserProfile } from "hooks/useUserProfile"
import { ModalLayout } from "layouts/ModalLayout"
import Inset from "components/common/Inset"
import ModalSectionTitle from "components/common/ModalSectionTitle/ModalSectionTitle"
import { TemperatureToggle } from "components/form/TemperatureToggle"
import CustomerNameContainer from "components/table/columnComponents/CustomerName/CustomerNameContainer"
import { AlertRulesTable } from "pages/contractor/MonitoringPage/AlertRulesTable"

import LiveDateView from "./LiveDataView"
import { StatsReportChartsWrapper } from "./StatsReportChartsWrapper"

import styles from "./index.module.scss"

interface Props {
  device: ApiTypes.Model.Device
  monitoringReportAnnotations?: MonitoringReportAnnotations | null
  isVendor?: boolean
  isCustomerReadOnly?: boolean
  onClearMonitoringReportAnnotations?: () => void
  onCancel: () => void
  defaultPeriod?: string
  defaultTab?: "liveData" | "reporting" | "alertRules"
}

export const StatsReportModal = ({
  device,
  monitoringReportAnnotations,
  isVendor,
  isCustomerReadOnly,
  onClearMonitoringReportAnnotations,
  onCancel,
  defaultPeriod,
  defaultTab,
}: Props) => {
  const { isContractor } = useUserProfile()
  const isAlertRulesEnabled = useAppSelector(isFeatureEnabled(FeatureFlag.AlertRules))
  const [activeTab, setActiveTab] = useState<"liveData" | "reporting" | "alertRules">(
    defaultTab && (defaultTab !== "alertRules" || isAlertRulesEnabled)
      ? defaultTab
      : monitoringReportAnnotations
        ? "reporting"
        : "liveData",
  )
  const userTemperatureScale = useAppSelector(state => state.user.userTemperatureScale)
  const isCelsius = userTemperatureScale === TemperatureScale.CELSIUS

  if (!device) return null

  const renderModalContent = () => {
    switch (activeTab) {
      case "liveData":
        return <LiveDateView device={device} isCelsius={isCelsius} />
      case "reporting":
        return (
          <StatsReportChartsWrapper
            device={device}
            isCelsius={isCelsius}
            monitoringReportAnnotations={monitoringReportAnnotations}
            onClearMonitoringReportAnnotations={onClearMonitoringReportAnnotations}
            defaultPeriod={defaultPeriod}
          />
        )
      case "alertRules":
        return isAlertRulesEnabled ? (
          <Inset left="md" right="md">
            <AlertRulesTable deviceId={device.id} />
          </Inset>
        ) : (
          <></>
        )
      default:
        return <></>
    }
  }

  const renderCustomerName = () => {
    if (isCustomerReadOnly) {
      const customerName = device?.homeowner_name
      if (customerName) {
        return (
          <div className={styles.devicesDataItem}>
            <h4 className={styles.modalSubtitle}>Customer</h4>
            <div className={styles.devicesDataText}>{customerName}</div>
          </div>
        )
      }

      return <></>
    }

    return (
      <div className={styles.devicesDataItem}>
        <h4 className={styles.modalSubtitle}>Customer</h4>
        <div className={styles.devicesDataText}>
          <CustomerNameContainer
            id={device?.client_id}
            name={device?.homeowner_name}
            isInstalled={true}
            deviceSN={device?.sn}
            showDashOnEmptyName
            isVendor={isVendor}
          />
        </div>
      </div>
    )
  }

  return (
    <>
      {createPortal(
        <ModalLayout buttonSize="auto" onClose={onCancel} isDrawer isWithoutFooter>
          <div className={styles.modalContent}>
            <h3 className={styles.modalTitle}>Monitoring</h3>
          </div>
          <div className={styles.tempToggleWrapper}>
            <TemperatureToggle />
          </div>
          <div className={styles.statsScrollableArea}>
            <ModalSectionTitle title="Details" />
            <div className={classNames(styles.monitoringReportContent)}>
              <div className={classNames(styles.devicesDataContainer)}>
                {renderCustomerName()}
                <div className={styles.devicesDataItem}>
                  <h4 className={styles.modalSubtitle}>Zip Code</h4>
                  <div className={styles.devicesDataText}>{device?.zip?.code || "-"}</div>
                </div>
                <div className={styles.devicesDataItem}>
                  <h4 className={styles.modalSubtitle}>Email</h4>
                  <div className={styles.devicesDataText}>{device?.email || "-"}</div>
                </div>
                <div className={styles.devicesDataItem}>
                  <h4 className={styles.modalSubtitle}>Thermostat S/N</h4>
                  <div className={styles.devicesDataText}>{device?.sn}</div>
                </div>
                {/* <div className={styles.devicesDataItem}>
                  <h4 className={styles.modalSubtitle}>Mobile App</h4>
                  <div className={styles.devicesDataText}>Activated</div>
                </div> */}
              </div>
            </div>
            <div className={styles.drawerTabsWrapper}>
              <div className={styles.drawerTabs}>
                <div
                  className={classNames(styles.drawerTab, { [styles.active]: activeTab === "liveData" })}
                  onClick={() => setActiveTab("liveData")}
                >
                  Live Data
                </div>
                <div
                  className={classNames(styles.drawerTab, { [styles.active]: activeTab === "reporting" })}
                  onClick={() => setActiveTab("reporting")}
                >
                  Reporting
                </div>
                {isAlertRulesEnabled && isContractor && (
                  <div
                    className={classNames(styles.drawerTab, { [styles.active]: activeTab === "alertRules" })}
                    onClick={() => setActiveTab("alertRules")}
                  >
                    Alert Rules
                  </div>
                )}
              </div>
            </div>
            {renderModalContent()}
          </div>
        </ModalLayout>,
        document.body,
      )}
    </>
  )
}
